import { useMemo } from 'react'
import { BarChartWrapper } from '../../charts/BarChart'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import { buildDrillRsql } from '../../drill'
import { formatCount } from '../../format'
import { buildAgingBarRows } from './analyticsMappers'
import type { AgingStatusBucket } from '../../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.caseAgingByStatus

const BUCKET_LABELS: Record<string, string> = {
  '0-7': '0-7 days',
  '8-30': '8-30 days',
  '31-90': '31-90 days',
  '90+': '90+ days',
  unknown: 'Unknown',
}

/**
 * AC4.1 — "Case aging by current status": a bar of case count bucketed by age-in-current-status
 * (0-7 / 8-30 / 31-90 / 90d+), plus an honest "Unknown" bar (never silently dropped) when the
 * backend returns at least one filing whose age could not be derived. A bar click cross-filters (no
 * server-side `ageBucket` filter dimension exists yet — this drills to the filtered case list
 * instead, mirroring `FilingsByStatusWidget`'s own drill-not-crossfilter posture for a dimension the
 * backend doesn't expose as a filter).
 */
export function CaseAgingWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const { status, data, message, retry } =
    useWidgetData<AgingStatusBucket[]>(WIDGET_KEY_SELF, filter, skipRefetch)

  const rows = useMemo(() => buildAgingBarRows(Array.isArray(data) ? data : []), [data])
  const total = rows.reduce((sum, r) => sum + r.count, 0)
  const frameState = status === 'ok' && total === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Case aging by current status"
      icon="clock"
      state={frameState}
      message={message ?? 'No cases in a non-terminal state for the current filter.'}
      onRetry={retry}
      drill={{ label: 'View cases', onClick: () => onDrill(buildDrillRsql(filter)) }}
    >
      <BarChartWrapper
        title="Case aging by current status"
        data={rows.map((r) => ({ ...r, label: BUCKET_LABELS[r.ageBucket] ?? r.ageBucket }))}
        categoryKey="label"
        categoryLabel="Age in current status"
        series={[{ key: 'count', label: 'Cases' }]}
        valueFormatter={(v) => formatCount(v)}
      />
      {rows.some((r) => r.ageBucket === 'unknown') ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          Some filings' age could not be derived and are counted under "Unknown" — never silently dropped.
        </p>
      ) : null}
    </WidgetFrame>
  )
}
